import React from 'react';
import { API_CONFIG } from '../../../config/api';
import {
    Show,
    SimpleShowLayout,
    useTranslate,
    usePermissions,
    useRecordContext,
    useRefresh,
    useNotify,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Paper,
    Divider,
    Alert,
    IconButton,
    Card,
    CardContent,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Button,
} from '@mui/material';
import {
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Send as SendingIcon,
    Refresh as RetryIcon,
    Receipt as ReceiptIcon,
    EvStation as ChargingIcon,
    Battery4Bar as BatteryIcon,
    Payment as PaymentIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    ArrowBack,
    Timeline,
    Info,
    Language as LanguageIcon,
    Description as TemplateIcon,
    Loop as RetryCountIcon,
    NotificationImportant,
    AccountCircle,
    Fingerprint,
    Category,
    CalendarToday,
    CheckCircleOutline,
    ErrorOutline,
    MonetizationOn,
    DirectionsCar,
    Bolt,
} from '@mui/icons-material';

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon />,
    sms: <SmsIcon />,
    whatsapp: <WhatsAppIcon />,
    push: <PushIcon />,
    slack: <SlackIcon />,
};

const statusIcons: Record<string, React.ReactElement> = {
    queued: <PendingIcon />,
    sending: <SendingIcon />,
    sent: <SuccessIcon />,
    delivered: <SuccessIcon />,
    failed: <ErrorIcon />,
    bounced: <ErrorIcon />,
};

const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    const colorMap: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
        queued: 'warning',
        sending: 'info',
        sent: 'success',
        delivered: 'success',
        failed: 'error',
        bounced: 'error',
    };
    return colorMap[status] || 'default';
};

const getChannelColor = (channel: string): string => {
    const colors: Record<string, string> = {
        email: '#1976D2',
        sms: '#4CAF50',
        whatsapp: '#25D366',
        push: '#FF6B35',
        slack: '#4A154B',
    };
    return colors[channel] || '#757575';
};

const formatCurrency = (amount: number, currency: string = 'TWD'): string => {
    return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};

const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

    return parts.join(' ');
};

const textWrapSx = {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
};

const infoPaperBaseSx = {
    p: 2,
    minWidth: 0,
    maxWidth: '100%',
    width: { xs: '100%', sm: 'auto' },
};

const wrappedChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        ...textWrapSx,
        lineHeight: 1.25,
        py: 0.5,
    },
};

// Page header component
const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const refresh = useRefresh();
    const notify = useNotify();
    const { permissions } = usePermissions();

    if (!record) return null;

    const handleRetry = async () => {
        try {
            const response = await fetch(`${API_CONFIG.baseURL}/api/backend/notification-logs/${record.id}/retry`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.ok) {
                notify('resources.notification-logs.messages.retry_success', { type: 'success' });
                refresh();
            } else {
                notify('resources.notification-logs.messages.retry_error', { type: 'error' });
            }
        } catch (error) {
            notify('resources.notification-logs.messages.retry_error', { type: 'error' });
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
            flexWrap: 'wrap',
            gap: { xs: 1.5, sm: 0 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/notification-logs')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                        },
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, ...textWrapSx }}>
                        {translate('resources.notification-logs.show.title')}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={textWrapSx}>
                        ID: {record.id}
                    </Typography>
                </Box>
            </Box>
            {record.status === 'failed' && permissions?.includes('notifications:write:all') && (
                <Button
                    onClick={handleRetry}
                    startIcon={<RetryIcon />}
                    variant="contained"
                    sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
                >
                    {translate('resources.notification-logs.actions.retry')}
                </Button>
            )}
        </Box>
    );
};

// Status and Basic Information
const StatusInformation = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1.5 }}>
                    <NotificationImportant sx={{ mr: 2, color: '#1976D2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                        {translate('resources.notification-logs.show.sections.status')}
                    </Typography>
                    <Box sx={{ ml: { xs: 0, sm: 'auto' }, display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                        <Chip
                            icon={statusIcons[record.status]}
                            label={translate(`resources.notification-logs.status.${record.status}`)}
                            color={getStatusColor(record.status)}
                            size="medium"
                            sx={wrappedChipSx}
                        />
                        <Chip
                            icon={channelIcons[record.channel]}
                            label={translate(`resources.notification-logs.channels.${record.channel}`)}
                            sx={{
                                backgroundColor: getChannelColor(record.channel) + '20',
                                color: getChannelColor(record.channel),
                                '& .MuiChip-icon': {
                                    color: getChannelColor(record.channel),
                                },
                                ...wrappedChipSx,
                            }}
                            size="medium"
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'stretch' }}>
                    <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#F8F9FA' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Category sx={{ fontSize: 20, color: '#1976D2', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Type
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                            {record.type || '-'}
                        </Typography>
                    </Paper>

                    {record.template && (
                        <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#E8F5E9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TemplateIcon sx={{ fontSize: 20, color: '#00C853', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Template
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                                {record.template}
                            </Typography>
                        </Paper>
                    )}

                    {record.language && (
                        <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 150px' }, backgroundColor: '#FFF8E1' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LanguageIcon sx={{ fontSize: 20, color: '#FF9800', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Language
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                                {record.language.toUpperCase()}
                            </Typography>
                        </Paper>
                    )}

                    {record.retry_count > 0 && (
                        <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 150px' }, backgroundColor: '#FFEBEE' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <RetryCountIcon sx={{ fontSize: 20, color: '#F44336', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Retry Count
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#F44336', ...textWrapSx }}>
                                {record.retry_count} {record.retry_count === 1 ? 'attempt' : 'attempts'}
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// Recipient Information
const RecipientInformation = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    const getRecipientTypeColor = (type: string) => {
        switch (type) {
            case 'app_user': return '#4CAF50';
            case 'backend_user': return '#2196F3';
            case 'external': return '#FF9800';
            default: return '#757575';
        }
    };

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                    <AccountCircle sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                        {translate('resources.notification-logs.show.sections.recipient')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'stretch' }}>
                    <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 250px' }, backgroundColor: '#E3F2FD' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 20, color: '#1976D2', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Recipient
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, ...textWrapSx }}>
                            {record.recipientName || record.recipient_id}
                        </Typography>
                        {record.recipientName && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={textWrapSx}>
                                ID: {record.recipient_id}
                            </Typography>
                        )}
                    </Paper>

                    <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#E8EAF6' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Category sx={{ fontSize: 20, color: '#5E35B1', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Type
                            </Typography>
                        </Box>
                        <Chip
                            label={translate(`resources.notification-logs.recipient_types.${record.recipient_type}`)}
                            size="small"
                            sx={{
                                backgroundColor: getRecipientTypeColor(record.recipient_type) + '20',
                                color: getRecipientTypeColor(record.recipient_type),
                                fontWeight: 600,
                                ...wrappedChipSx,
                            }}
                        />
                    </Paper>
                </Box>
            </CardContent>
        </Card>
    );
};

// Timestamps
const TimestampsInformation = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;

    const calculateDeliveryTime = () => {
        if (!record.created_at || !record.delivered_at) return null;
        const created = new Date(record.created_at).getTime();
        const delivered = new Date(record.delivered_at).getTime();
        const diffMs = delivered - created;
        const diffSecs = Math.floor(diffMs / 1000);
        return formatDuration(diffSecs);
    };

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                    <Timeline sx={{ mr: 2, color: '#2196F3' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                        {translate('resources.notification-logs.show.sections.timestamps')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'stretch' }}>
                    <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 220px' }, backgroundColor: '#F8F9FA' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CalendarToday sx={{ fontSize: 20, color: '#757575', mr: 1 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Created
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                            {record.created_at && new Date(record.created_at).toLocaleString('zh-TW')}
                        </Typography>
                    </Paper>

                    {record.sent_at && (
                        <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 220px' }, backgroundColor: '#E3F2FD' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SendingIcon sx={{ fontSize: 20, color: '#2196F3', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Sent
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                                {new Date(record.sent_at).toLocaleString('zh-TW')}
                            </Typography>
                        </Paper>
                    )}

                    {record.delivered_at && (
                        <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 220px' }, backgroundColor: '#E8F5E9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircleOutline sx={{ fontSize: 20, color: '#4CAF50', mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Delivered
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, ...textWrapSx }}>
                                {new Date(record.delivered_at).toLocaleString('zh-TW')}
                            </Typography>
                            {calculateDeliveryTime() && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={textWrapSx}>
                                    in {calculateDeliveryTime()}
                                </Typography>
                            )}
                        </Paper>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// Error Display
const ErrorDisplay = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record?.error) return null;

    return (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" gutterBottom>
                {translate('resources.notification-logs.show.sections.error')}
            </Typography>
            <Typography variant="body2" sx={textWrapSx}>
                {record.error}
            </Typography>
        </Alert>
    );
};

// Notification Data Display
const NotificationData = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record?.data) return null;

    const data = record.data;
    const sessionSummary = data.session_summary || null;
    const paymentSummary = data.payment_summary || null;

    // Special handling for payment receipts
    if (record.type === 'payment.receipt' || sessionSummary) {
        return (
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                        <ReceiptIcon sx={{ mr: 2, color: '#1976D2' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Charging Receipt
                        </Typography>
                    </Box>

                    {/* Session Summary */}
                    {sessionSummary && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, ...textWrapSx }}>
                                Session Information
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2 }, alignItems: 'stretch' }}>
                                <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#E8F5E9' }}>
                                    <Typography variant="caption" color="text.secondary">Energy</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#00C853' }}>
                                        {sessionSummary.energy_kwh ? `${sessionSummary.energy_kwh.toFixed(2)} kWh` : '-'}
                                    </Typography>
                                </Paper>

                                <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 200px' }, backgroundColor: '#E3F2FD' }}>
                                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976D2' }}>
                                        {sessionSummary.duration_seconds ? formatDuration(sessionSummary.duration_seconds) : '-'}
                                    </Typography>
                                </Paper>

                                <Paper sx={{ ...infoPaperBaseSx, flex: { xs: '1 1 100%', sm: '1 1 250px' }, backgroundColor: '#F8F9FA' }}>
                                    <Typography variant="caption" color="text.secondary">Charger</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, ...textWrapSx }}>
                                        {sessionSummary.charger_name || sessionSummary.charger_id || '-'}
                                    </Typography>
                                    {sessionSummary.location && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={textWrapSx}>
                                            <LocationIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                                            {' '}{sessionSummary.location}
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        </Box>
                    )}

                    {/* Payment Summary */}
                    {paymentSummary && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, ...textWrapSx }}>
                                Payment Details
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: '#FFF8E1' }}>
                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', '& td': { border: 0, py: 0.5, ...textWrapSx } }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ pl: 0 }}>Energy Cost</TableCell>
                                            <TableCell align="right" sx={{ pr: 0 }}>
                                                {formatCurrency(paymentSummary.energy_cost || 0, paymentSummary.currency)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ pl: 0 }}>Service Fee</TableCell>
                                            <TableCell align="right" sx={{ pr: 0 }}>
                                                {formatCurrency(paymentSummary.service_fee || 0, paymentSummary.currency)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ pl: 0 }}>Parking Fee</TableCell>
                                            <TableCell align="right" sx={{ pr: 0 }}>
                                                {formatCurrency(paymentSummary.parking_fee || 0, paymentSummary.currency)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ pl: 0, pt: 1.5, borderTop: '2px solid #FF9800' }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>Total</Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ pr: 0, pt: 1.5, borderTop: '2px solid #FF9800' }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FF9800' }}>
                                                    {formatCurrency(paymentSummary.total_amount || 0, paymentSummary.currency)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    }

    // Generic data display
    const renderValue = (value: any): React.ReactNode => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'boolean') {
            return <Chip label={value ? 'Yes' : 'No'} size="small" color={value ? 'success' : 'default'} />;
        }
        if (typeof value === 'number') return <Typography component="span" sx={textWrapSx}>{value.toLocaleString()}</Typography>;
        if (typeof value === 'string') {
            if (value.startsWith('http')) {
                return (
                    <a
                        href={value}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976D2', wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                    >
                        {value}
                    </a>
                );
            }
            return <Typography component="span" sx={textWrapSx}>{value}</Typography>;
        }
        if (Array.isArray(value)) {
            return value.map((item, index) => (
                <Chip
                    key={index}
                    label={String(item)}
                    size="small"
                    variant="outlined"
                    sx={{
                        mr: 0.5,
                        mb: 0.5,
                        ...wrappedChipSx,
                    }}
                />
            ));
        }
        if (typeof value === 'object') {
            return (
                <Box sx={{ mt: 1, minWidth: 0 }}>
                    {Object.entries(value).map(([k, v]) => (
                        <Box key={k} sx={{ mb: 1, pl: 2, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, ...textWrapSx }}>
                                {k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                            </Typography>
                            <Box sx={{ pl: 2, minWidth: 0 }}>{renderValue(v)}</Box>
                        </Box>
                    ))}
                </Box>
            );
        }
        return <Typography component="span" sx={textWrapSx}>{String(value)}</Typography>;
    };

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                    <Info sx={{ mr: 2, color: '#2196F3' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, ...textWrapSx }}>
                        {translate('resources.notification-logs.show.sections.data')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                    {Object.entries(data).map(([key, value]) => (
                        <Paper key={key} sx={{ ...infoPaperBaseSx, width: '100%', backgroundColor: '#F8F9FA' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...textWrapSx }}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Box sx={{ minWidth: 0 }}>{renderValue(value)}</Box>
                        </Paper>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

export const LogShow = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canView = permissions?.includes('notifications:read:all');

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>
                    {translate('resources.notification-logs.permissions.no_access')}
                </Typography>
            </Box>
        );
    }

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                maxWidth: '100%',
                overflowX: 'hidden',
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    maxWidth: '100%',
                    overflowX: 'hidden',
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0, px: { xs: 1, sm: 2 } }}>
                    <PageHeader />

                    <StatusInformation />

                    <RecipientInformation />

                    <TimestampsInformation />

                    <ErrorDisplay />

                    <NotificationData />
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};
